import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alarmCardMapper, CustomBreadCrumbs, CustomTooltipWithLabel, DashboardHeader, FilterLayout, FilterProp, renderHeaderActionButtons, SearchBox, SidePopover, TableLayout, TableSubHeader, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { cloneDeep } from 'lodash';
import { fetchTroubleshootingListData, getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, getRmsSupportCenterReducer, setFilterData, setRmsTroubleshootingAlarmTableProps, setRmsTroubleshootingStopReasonTableProps } from '../../../rmsReduxStore/reduxExports';
import './TroubleShootingDashboard.css'
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { createBreadCrumbProps } from '../../../globalUtils/globalHooks';
import TroubleShootingRowCard from './TroubleShootingRowCard';
import StopReasonTroubleShootingRowCard from './StopReasonTroubleShootingRowCard';
import { NoDataComponentTroubleshooting } from '../../../globalUtils/TableDashboard/TableComponents';



type screenType = 'alarm' | 'stopReasonCode';
type layoutViewType = 'list' | 'detail';


const TroubleShootingDashboard: React.FC = ()=>{
    const dispatch = useAppDispatch();

    const {rmsSupportTroubleshootingAlarmList,rmsSupportTroubleshootingAlarmListCount,rmsSupportTroubleshootingAlarmTableProps, rmsSupportTroubleshootingListLoader,rmsSupportTroubleshootingStopReasonList,rmsSupportTroubleshootingStopReasonListCount,rmsSupportTroubleshootingStopReasonTableProps} = useAppSelector(getRmsSupportCenterReducer);
    const {rmsSupportTroubleshootingAlarmsReferenceData,alarmSeverityReferenceData,versionProductTypeReferenceData,chargingHistoryStopReasonCodeReferenceData} = useAppSelector(getReferenceDataReducer); 
    const { screenFilters } = useAppSelector(getFilterReducer);

    // const rmsSupportTroubleshootingAlarmListRef = useRef(rmsSupportTroubleshootingAlarmList)
    // const rmsSupportTroubleshootingStopReasonListRef = useRef(rmsSupportTroubleshootingStopReasonList)
    const rmsSupportTroubleshootingAlarmTablePropsRef = useRef(rmsSupportTroubleshootingAlarmTableProps);
    const rmsSupportTroubleshootingStopReasonTablePropsRef = useRef(rmsSupportTroubleshootingStopReasonTableProps);
    const [tableHeight, setTableHeight] = useState(0);

    const [filtersVisible, setFiltersVisible] = useState(false);
    const [activeScreen,setActiveScreen] = useState<screenType> ('alarm');
    const [layoutView,setLayoutView] = useState<layoutViewType>('list');
    const headerRef = useRef<HTMLDivElement | null>(null);
    const tableRef =  useRef<HTMLDivElement | null>(null);
    const dashboardRef = useRef<HTMLDivElement | null>(null);
    const breadcrumRef = useRef<HTMLDivElement | null>(null);
    const subheaderRef =  useRef<HTMLDivElement | null>(null);

    useEffect(()=>{
        rmsSupportTroubleshootingAlarmTablePropsRef.current = rmsSupportTroubleshootingAlarmTableProps
    },[rmsSupportTroubleshootingAlarmTableProps])

    useEffect(()=>{
        rmsSupportTroubleshootingStopReasonTablePropsRef.current = rmsSupportTroubleshootingStopReasonTableProps
    },[rmsSupportTroubleshootingStopReasonTableProps])

    const layoutCallback = useCallback(
        async (
          pageNumber: number,
          pageSize: number,
          view: string,
          field: string | null,
          order: string,
          filterData,
          searchTerm: string,
          fetchType?: string,
        ) => {
          fetchType = !fetchType ? activeScreen : fetchType
          const tableProps = fetchType === 'alarm' ? cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef.current) : cloneDeep(rmsSupportTroubleshootingStopReasonTablePropsRef.current) ;
          const rmsTroubleshootingFilter = fetchType === 'alarm' ? getCurrentFiltersValues('rmsSupportAlarmTroubleshooting', true) : getCurrentFiltersValues('rmsSupportStopReasonCodeTroubleshooting', true);
          const appliedFilters = filterData ? filterData : rmsTroubleshootingFilter;
          await dispatch(
            fetchTroubleshootingListData({
              view: view ?? layoutView,
              sortBy: field || tableProps?.sortBy,
              order: order || tableProps?.order,
              pageSize: pageSize,
              pageNumber: pageNumber,
              filters: appliedFilters,
              searchTerm: searchTerm != null ? searchTerm : tableProps?.searchTerm,
              fetchType: fetchType
            })
          );
    
          if (tableProps) {
            tableProps.view = view || layoutView
            tableProps.sortBy = field || tableProps?.sortBy;
            tableProps.order = order || tableProps?.order;
            tableProps.pageNumber = pageNumber;
            tableProps.pageSize = pageSize;
            if (searchTerm != null && searchTerm != undefined) {
              tableProps.searchTerm = searchTerm;
            }
          }
          if(fetchType === 'alarm')  await dispatch(setRmsTroubleshootingAlarmTableProps(tableProps))
          else await dispatch(setRmsTroubleshootingStopReasonTableProps(tableProps));
        },
        [rmsSupportTroubleshootingAlarmTablePropsRef?.current,rmsSupportTroubleshootingStopReasonTablePropsRef?.current, dispatch,layoutView, activeScreen]
    );


    const onChangeOfSearchText = useCallback(async (searchText: string) => {
        const trimmedSearchText = searchText.trim();
        const tableProps = activeScreen === 'alarm' ? cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef.current) : cloneDeep(rmsSupportTroubleshootingStopReasonTablePropsRef.current) ;
        if (tableProps) {
          tableProps.searchTerm = trimmedSearchText;
        }
        const rmsTroubleshootingFilter = activeScreen === 'alarm' ?  getCurrentFiltersValues('rmsSupportAlarmTroubleshooting', true): getCurrentFiltersValues('rmsSupportStopReasonCodeTroubleshooting', true) ;
        await layoutCallback(
          1,
          tableProps?.pageSize,
          tableProps?.view,
          tableProps?.sortBy,
          tableProps?.order,
          rmsTroubleshootingFilter,
          trimmedSearchText,
          activeScreen
        )
      }, [ rmsSupportTroubleshootingAlarmTablePropsRef?.current, rmsSupportTroubleshootingStopReasonTablePropsRef?.current, layoutCallback,activeScreen]);


    const searchBoxConfiguration = useMemo(() => {
        if (activeScreen === 'alarm' ) {
            return {
                searchBoxClassName: 'rms__support__troubleshooting__alarm__search__box',
                searchFieldId: 'rms-support-troubleshooting-alarm-search-box',
                searchFieldName: 'rms__support__troubleshooting__alarm__search-box',
                handleSearch: onChangeOfSearchText,
            }   
        }
        return {
          searchBoxClassName: 'rms__support__troubleshooting__stop__reason__search__box',
          searchFieldId: 'rms-support-troubleshooting-stop-reason-search-box',
          searchFieldName: 'rms__support__troubleshooting__stop__reason__search-box',
          handleSearch: onChangeOfSearchText,
        }
      }, [onChangeOfSearchText, rmsSupportTroubleshootingAlarmTablePropsRef?.current, rmsSupportTroubleshootingStopReasonTablePropsRef?.current, activeScreen]);


    const headerActionBtns = useMemo(()=>renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_filter_btn',
          isAccessible: true,
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: activeScreen === 'alarm' ? getCurrentScreenAppliedFiltersCount('rmsSupportAlarmTroubleshooting',true) : getCurrentScreenAppliedFiltersCount('rmsSupportStopReasonCodeTroubleshooting',true)
        },
      ],
    }),[activeScreen])

    const handleToggleScreenBtnClick = useCallback(
        (screen:screenType)=>{
            const tableProps = screen === 'alarm' ? cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef?.current) : cloneDeep(rmsSupportTroubleshootingStopReasonTablePropsRef?.current);
            layoutCallback(
                tableProps?.pageNumber,
                tableProps?.pageSize,
                layoutView,
                tableProps?.sortBy,
                tableProps?.order,
                null,
                '',
                screen
            )
            setActiveScreen(screen)
        }
    ,[rmsSupportTroubleshootingAlarmTablePropsRef?.current,rmsSupportTroubleshootingStopReasonTablePropsRef?.current,layoutView])

    const toggleActiveScreenButtonsConfiuration = [
        {
            id: 'alarmTroubleshootingBtn',
            btnClassName:activeScreen === 'alarm' ? 'rms-troubleshooting-screen-toggle-btn-selected rms-troubleshooting-screen-toggle-btn-left-border-radius' : 'rms-troubleshooting-screen-toggle-btn rms-troubleshooting-screen-toggle-btn-left-border-radius',
            buttonText:'Alarm Troubleshooting',
            onClick: ()=>{
                handleToggleScreenBtnClick('alarm')
            }
        },
        {
           id: 'stopReasonTroubleshootingBtn',
            btnClassName:activeScreen === 'stopReasonCode' ? 'rms-troubleshooting-screen-toggle-btn-selected rms-troubleshooting-screen-toggle-btn-right-border-radius' : 'rms-troubleshooting-screen-toggle-btn rms-troubleshooting-screen-toggle-btn-right-border-radius',
            buttonText:'Stop Reason Troubleshooting',
            onClick: ()=>{
                handleToggleScreenBtnClick('stopReasonCode')
            } 
        }
    ]


    const rmsSupportTroubleshootingTableColumns = useMemo(()=>{
        const alarmTroubleshootingTableColumns = [
        {
            field: 'severity',
            headerName: '',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            pinnable: false,
            minWidth: 20,
            maxWidth: 30,
            disableColumnMenu: true,
            width: 20,
            renderCell: (params) => {
                const rowData = params?.row;
                const severityIcon = rowData?.alarmStatus === 'No Alarm' ? 'TOTAL' : rowData?.severity?.toUpperCase();
                const tooltipText = rowData?.alarmStatus === 'No Alarm' ? 'No Alarm' : alarmCardMapper[severityIcon]?.title;
                return (
                <div className="charger__connector__type__cell">
                    <IconWithTooltip iconClassName={rowData?.alarmStatus === 'No Alarm' ? 'no_alarm_icon' : alarmCardMapper[severityIcon]?.alarmSummaryIcon}
                    toolTipText={tooltipText} />
                </div>
                );
            }
        },
        {
            field: 'alarm',
            headerName: 'Alarm',
            flex:1,
            sortable: true,
            pinnable: false,
            minWidth: 150,
            renderCell: (params) => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.alarm} labelId={rowData?.id} />
                )
            }
        },
        {
            field: 'productTypes',
            headerName: 'Product Type',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 300,
            renderCell: (params) => {
                const rowData = params?.row
                const productTypeList = rowData?.productTypes?.map(productType=>{
                    let backgroundColor = '';
                    let color = '';
                    let borderColor = '';
                    switch(productType) {
                        case 'Harmony Gen1.0':
                            color = '#713EFF';
                            backgroundColor = '#E1E1FF';
                            borderColor = '#713EFF7A'
                            break;
                        case 'EVM150':
                            color = '#128FED';
                            backgroundColor = '#E5F3FE';
                            borderColor = '#128FED7A'
                            break;
                        case 'Harmony Gen2.0':
                            color = '#E6901F';
                            backgroundColor = '#FCF5DB';
                            borderColor = '#E6901F7A'
                            break;
                    }
                    return (
                        <span key={productType}
                        style={
                            {
                                color,
                                backgroundColor,
                                border: `1px solid ${borderColor}`,
                                fontSize: '12px',
                                letterSpacing:'-1.6%',
                                borderRadius: '6px',
                                padding:'2px 4px'
                            }
                        }
                        >{productType}</span>
                    )
                })
                return  (
                    <span 
                        style={{
                            display:'flex',
                            gap:'2px'
                        }}
                    >
                        {productTypeList}
                    </span>
                )
            }
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            sortable: false,
            pinnable: false,
            minWidth: 250,
            renderCell: (params) => {
                const rowData = params?.row
                return  (
                <CustomTooltipWithLabel label={rowData?.description} labelId={rowData?.id} />
                )
            }
        },
        {
            field: 'errorCode',
            headerName: 'Error Code',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params) => {
                const rowData = params?.row
                return  (
                    <CustomTooltipWithLabel label={rowData?.errorCode} labelId={rowData?.id} />
                )
            }
        },
        {
            field: 'vendorErrorCode',
            headerName: 'Vendor Error Code',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params) => {
                const rowData = params?.row
                return  (
                    <CustomTooltipWithLabel label={rowData?.vendorErrorCode?.toString()} labelId={rowData?.id} />
                )
            }
        },
        {
            field: 'l1Action',
            headerName: 'L1 Action',
            flex: 1,
            sortable: false,
            pinnable: false,
            minWidth: 250,
            renderCell: (params) => {
                const rowData = params?.row
                return  (
                <CustomTooltipWithLabel label={rowData?.l1Action} labelId={rowData?.id} />
                )
            }
        },
        ]
        const stopReasonTroubleshootingTableColumns = [
            {
                field: 'stopReason',
                headerName: 'Stop Reason',
                flex:1,
                sortable: true,
                pinnable: false,
                minWidth: 150,
                renderCell: (params) => {
                    const rowData = params?.row
                    return (
                        <CustomTooltipWithLabel label={rowData?.stopReason} labelId={rowData?.id} />
                    )
                }
            },
            {
                field: 'stoppedBy',
                headerName: 'Stopped By',
                flex:1,
                sortable: true,
                pinnable: false,
                minWidth: 100,
                renderCell: (params) => {
                    const rowData = params?.row
                    return (
                        <span style={{fontWeight:500}}>{rowData?.stoppedBy}</span>
                    )
                }
            },
            {
                field: 'productTypes',
                headerName: 'Product Type',
                flex: 1,
                sortable: true,
                pinnable: false,
                minWidth: 300,
                renderCell: (params) => {
                    const rowData = params?.row
                    const productTypeList = rowData?.productTypes?.map(productType=>{
                        let backgroundColor = '';
                        let color = '';
                        let borderColor = '';
                        switch(productType) {
                            case 'Harmony Gen1.0':
                                color = '#713EFF';
                                backgroundColor = '#E1E1FF';
                                borderColor = '#713EFF7A'
                                break;
                            case 'EVM150':
                                color = '#128FED';
                                backgroundColor = '#E5F3FE';
                                borderColor = '#128FED7A'
                                break;
                            case 'Harmony Gen2.0':
                                color = '#E6901F';
                                backgroundColor = '#FCF5DB';
                                borderColor = '#E6901F7A'
                                break;
                        }
                        return (
                            <span key={productType}
                            style={
                                {
                                    color,
                                    backgroundColor,
                                    border: `1px solid ${borderColor}`,
                                    fontSize: '12px',
                                    letterSpacing:'-1.6%',
                                    borderRadius: '6px',
                                    padding:'2px 4px'
                                }
                            }
                            >{productType}</span>
                        )
                    })
                    return  (
                        <span 
                            style={{
                                display:'flex',
                                gap:'2px'
                            }}
                        >
                            {productTypeList}
                        </span>
                    )
                }
            },
            {
                field: 'description',
                headerName: 'Description',
                flex: 1,
                sortable: false,
                pinnable: false,
                minWidth: 250,
                renderCell: (params) => {
                    const rowData = params?.row
                    return  (
                    <CustomTooltipWithLabel label={rowData?.description} labelId={rowData?.id} />
                    )
                }
            },
            {
                field: 'errorCode',
                headerName: 'Error Code',
                flex: 1,
                sortable: true,
                pinnable: false,
                minWidth: 120,
                renderCell: (params) => {
                    const rowData = params?.row
                    return  (
                        <CustomTooltipWithLabel label={rowData?.errorCode} labelId={rowData?.id} />
                    )
                }
            },
            {
                field: 'vendorErrorCode',
                headerName: 'Vendor Error Code',
                flex: 1,
                sortable: true,
                pinnable: false,
                minWidth: 120,
                renderCell: (params) => {
                    const rowData = params?.row
                    return  (
                        <CustomTooltipWithLabel label={rowData?.vendorErrorCode?.toString()} labelId={rowData?.id} />
                    )
                }
            },
            {
                field: 'l1Action',
                headerName: 'L1 Action',
                flex: 1,
                sortable: false,
                pinnable: false,
                minWidth: 250,
                renderCell: (params) => {
                    const rowData = params?.row
                    return  (
                    <CustomTooltipWithLabel label={rowData?.l1Action} labelId={rowData?.id} />
                    )
                }
            },
    ]
    if(activeScreen === 'alarm')return alarmTroubleshootingTableColumns;
    else return stopReasonTroubleshootingTableColumns;
    },[activeScreen])

    const getTableRows = useCallback((): IRmsSupportTroubleshootingAlarmListItem[] | IRmsSupportTroubleshootingStopReasonCodeListItem[] => {
        if(activeScreen === 'alarm')return rmsSupportTroubleshootingAlarmList !== null && rmsSupportTroubleshootingAlarmList?.length > 0 ? rmsSupportTroubleshootingAlarmList : []
        else return rmsSupportTroubleshootingStopReasonList !== null && rmsSupportTroubleshootingStopReasonList?.length > 0 ? rmsSupportTroubleshootingStopReasonList : []
      }, [rmsSupportTroubleshootingAlarmList,rmsSupportTroubleshootingStopReasonList,activeScreen])

    const handleFiltersClosePopOver = (): void => {
        setFiltersVisible(false);
    }

    const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
        const tableProps = activeScreen === 'alarm' ? cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef.current) : cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef.current);
        const filterScreen = activeScreen === 'alarm' ? 'rmsSupportAlarmTroubleshooting' : 'rmsSupportStopReasonCodeTroubleshooting'
        const res = await setFilterData(data, filterKey, filterScreen, 'SET', isGlobal, true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
          await layoutCallback?.(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            res?.filters,
            tableProps?.searchTerm ?? '',
            activeScreen ?? 'alarm'
          )
        }
      }, [rmsSupportTroubleshootingAlarmTablePropsRef?.current, rmsSupportTroubleshootingStopReasonTablePropsRef?.current,activeScreen]);

        const rmsSupportTroubleShootingFilterProps:FilterProp[] = useMemo(()=>{
        const alarmTroubleshootingFilters: FilterProp[] =  [
            {
                filterLabel: 'Alarms',
                filterId: 'rmsSupportTroubleshootingAlarm',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingAlarm', 'rmsSupportAlarmTroubleshooting', false),
                    selectOptions: rmsSupportTroubleshootingAlarmsReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingAlarm', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-alarm-type-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Severity',
                filterId: 'rmsSupportTroubleshootingSeverity',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingSeverity', 'rmsSupportAlarmTroubleshooting', false),
                    selectOptions: alarmSeverityReferenceData?.filter((item) => item.id !== 'No Alarm') ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingSeverity', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-severity-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Product Type',
                filterId: 'rmsSupportTroubleshootingProductType',
                filterType:'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingProductType', 'rmsSupportAlarmTroubleshooting', false),
                    selectOptions: versionProductTypeReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingProductType', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-product-type-filter-dropdown',
                    isMultiSelect: true
                }

            }
        ]
        const stopReasonCodeTroubleshootingFilters: FilterProp[] = [
            {
                filterLabel: 'Stop Reason Code',
                filterId: 'rmsSupportTroubleshootingStopReasonCode',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingStopReasonCode', 'rmsSupportStopReasonCodeTroubleshooting', false),
                    selectOptions: chargingHistoryStopReasonCodeReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingStopReasonCode', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-stop-reason-code-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
               filterLabel: 'Stopped By',
                filterId: 'rmsSupportTroubleshootingStoppedBy',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingStoppedBy', 'rmsSupportStopReasonCodeTroubleshooting', false),
                    selectOptions: ['All','Charger','CMS','User','Vehicle']?.map(item=>({id:item,name:item,label:item,value:item})) ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingStoppedBy', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-stopped-by-filter-dropdown',
                    isMultiSelect: true
                } 
            },
            {
                filterLabel: 'Product Type',
                filterId: 'rmsSupportTroubleshootingProductType',
                filterType:'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportTroubleshootingProductType','rmsSupportStopReasonCodeTroubleshooting', false),
                    selectOptions: versionProductTypeReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                    handleFilterDataChange(val, 'rmsSupportTroubleshootingProductType', false)
                    },
                    selectDropDownId: 'rms-support-troubleshooting-product-type-filter-dropdown',
                    isMultiSelect: true
                }

            }
        ]
        if (activeScreen === 'alarm')return alarmTroubleshootingFilters;
        else return stopReasonCodeTroubleshootingFilters;
    },[rmsSupportTroubleshootingAlarmsReferenceData,alarmSeverityReferenceData,handleFilterDataChange,screenFilters,activeScreen])

    const onResetButtonClick = useCallback(async (callList: boolean) => {
        const filterScreen = activeScreen === 'alarm' ? 'rmsSupportAlarmTroubleshooting' : 'rmsSupportStopReasonCodeTroubleshooting'
        const res = await setFilterData(null, '', filterScreen, 'CLEAR', true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
        // Reset the filters
        if (callList && (rmsSupportTroubleshootingAlarmTablePropsRef?.current || rmsSupportTroubleshootingStopReasonTablePropsRef?.current)) {
            const tableProps = activeScreen === 'alarm' ? cloneDeep(rmsSupportTroubleshootingAlarmTablePropsRef.current) : cloneDeep(rmsSupportTroubleshootingStopReasonTablePropsRef.current) ;
            await layoutCallback(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            res?.filters,
            tableProps?.searchTerm ?? '',
            activeScreen
            )
        }
        }
    
    }, [rmsSupportTroubleshootingAlarmTablePropsRef?.current,rmsSupportTroubleshootingStopReasonTablePropsRef?.current,activeScreen])

    const filterFooterBtns = useMemo(() => {
        return [
        {
            buttonText: 'Reset',
            buttonId: 'Reset',
            btnClassName: 'secondary__btn',
            handleClick: (): Promise<void> => onResetButtonClick(true),
            isDisabled: false,
            buttonVariant: 'outlined',
        },
        ];
    }, [onResetButtonClick]);

    const breadCrumbArray = useMemo(()=>createBreadCrumbProps(
        {
            breadCrumbProps: [
                {
                    objectType: 'link',
                    id: 'supportCentre',
                },
                {
                    objectType: 'text',
                    id:'text',
                    text: 'Troubleshooting'
                }
            ]
        }
    ),[])

    const handleLayoutToggle = (val:layoutViewType) => {
        setLayoutView(val)
    }

    const layoutsConfiguration = useMemo(() => {
        return [
          {
            toolTipText: 'List View',
            onIconClick: (): void => {
              handleLayoutToggle('list');
            },
            layout: 'list',
            iconClassName: 'rms__table__dashboard-list__view__icon',
          },
          {
            toolTipText: 'Detailed View',
            onIconClick: (): void => {
              handleLayoutToggle('detail');
            },
            layout: 'detail',
            iconClassName: 'rms__table__dashboard-detailed__view__icon',
          }
    
        ];
      }, [handleLayoutToggle]);
      
    const calculateContentHeight = useCallback(()=>{
        if (dashboardRef?.current && headerRef?.current && breadcrumRef?.current && subheaderRef?.current) {
            const dashboardRect = dashboardRef?.current?.getBoundingClientRect();
            const headerRect = headerRef?.current?.getBoundingClientRect();
            const dashboardHeight = dashboardRect?.height;
            const headerHeight = headerRect?.height;
            const newTableHeight = dashboardHeight - headerHeight;
            setTableHeight(newTableHeight);
        }

    },[dashboardRef?.current,headerRef?.current,breadcrumRef?.current])


    useEffect(()=>{
        const ro = new ResizeObserver(()=>{
            calculateContentHeight();
        });

        if(dashboardRef?.current) ro.observe(dashboardRef?.current);
        if(headerRef?.current) ro.observe(headerRef?.current);

        return ()=>{
            ro.disconnect();
        }
    },[calculateContentHeight])

    return (
        <div className='rms__support__troubleshoot__dashboard__container' ref={dashboardRef}>   
            <div className='rms__support__troubleshoot__dashboard__container__header' ref={headerRef}>
                <div className='help__and__faq__topics__dashboard__header-bread-crumb-container'>
                    <CustomBreadCrumbs breadCrumbs={breadCrumbArray} />
                </div>
                <div className='rms__support__troubleshoot__dashboard__header' >
                    <div className='rms__support__troubleshoot__dashboard__header-left'>
                        <DashboardHeader header="Troubleshooting" />
                    </div>
                    <div className='rms__support__troubleshoot__dashboard__header-right'>
                        <TableSubHeader headerActionBtns={headerActionBtns}/>
                        <div className='rms__support__troubleshoot__dashboard__header-right-toggle-screen-btns'>
                            {
                                toggleActiveScreenButtonsConfiuration?.map(btn=>{
                                    return (
                                    <div key={btn?.id} className={btn?.btnClassName}
                                    onClick={btn?.onClick}
                                    >
                                        {btn?.buttonText}
                                    </div>)
                                })
                            }
                        </div>
                    </div>
                </div>
                <div className='rms__support__troubleshoot__dashboard__sub__header'>
                    <div className='rms__support__troubleshoot__dashboard__search-box-container'>
                        <SearchBox
                            key={searchBoxConfiguration?.searchFieldId}
                            searchFieldId={searchBoxConfiguration?.searchFieldId ?? ''}
                            searchFieldName={searchBoxConfiguration?.searchFieldName ?? ''}
                            searchBoxClassName={searchBoxConfiguration?.searchBoxClassName ?? ''}
                            handleSearch={searchBoxConfiguration?.handleSearch}
                        />
                    </div>
                    <div className='rms__support__troubleshoot__dashboard__sub__header-right'>
                        <div className='rms__support__troubleshoot__dashboard__sub__header-right-layout-toggle-btns'>
                            {
                                layoutsConfiguration?.map((layout, index) => {
                                    return (
                                        <IconWithTooltip
                                        key={index}
                                        toolTipText={layout.toolTipText}
                                        onIconClick={layout.onIconClick}
                                        iconClassName={
                                            layoutView === layout.layout
                                            ? layout.iconClassName + ' selected'
                                            : layout.iconClassName
                                        }
                                        />
                                    );
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className='rms__support__troubleshoot__dashboard__container-body' ref={tableRef} style={{height:`${tableHeight}px`}}>
                <TableLayout
                    key={'rms__support__troubleshoot__dashboard-table-'+layoutView}
                    tableId='rms__support__troubleshoot__dashboard-table'
                    tableClassName='rms__support__troubleshoot__dashboard__table'
                    gridColumns={rmsSupportTroubleshootingTableColumns}
                    tableRows={getTableRows()}
                    layoutView={layoutView}
                    renderCustomRowComponent={layoutView === 'detail'}
                    disableHeader= {layoutView === 'detail'}
                    customRowComponent={activeScreen === 'alarm' ? TroubleShootingRowCard : StopReasonTroubleShootingRowCard}
                    listLayoutCallBack={layoutCallback}
                    totalCount={rmsSupportTroubleshootingListLoader ? 0 : activeScreen === 'alarm' ? rmsSupportTroubleshootingAlarmListCount : rmsSupportTroubleshootingStopReasonListCount}
                    customNoDataComponent={NoDataComponentTroubleshooting}
                    totalCountText={'Troubleshoots'}
                    showSkeleton={!rmsSupportTroubleshootingAlarmList}
                    showLoader={rmsSupportTroubleshootingListLoader}
                    pageNumber={activeScreen === 'alarm'?rmsSupportTroubleshootingAlarmTableProps?.pageNumber : rmsSupportTroubleshootingStopReasonTableProps?.pageNumber}
                    tablePageSize={activeScreen === 'alarm'?rmsSupportTroubleshootingAlarmTableProps?.pageSize : rmsSupportTroubleshootingStopReasonTableProps?.pageSize}
                    sortField={activeScreen === 'alarm'?rmsSupportTroubleshootingAlarmTableProps?.sortBy : rmsSupportTroubleshootingStopReasonTableProps?.sortBy}
                    sortOrder={activeScreen === 'alarm'?rmsSupportTroubleshootingAlarmTableProps?.order : rmsSupportTroubleshootingStopReasonTableProps?.order}
                />
            
            </div>
        <>
        {
          filtersVisible && <SidePopover
            id={'rms-support-troubleshooting-filters'}
            open={filtersVisible}
            containerClassName={'rms-support-troubleshootings-side-filters'}
            handleClose={handleFiltersClosePopOver}
            headerText={'Add Filters'}
            // eslint-disable-next-line react/no-children-prop
            children={<FilterLayout requiredReferenceDataCallbacks={['rmsSupportTroubleshootingAlarm','severity','versionProductType','chargingHistoryStopReasonCode']}
              filters={rmsSupportTroubleShootingFilterProps} handleFilterDataChange={handleFilterDataChange}
              excludeGlobalFilters={true}
            />}
            btnsList={filterFooterBtns}
          />
        }
      </>
        </div>
    )
}



export default TroubleShootingDashboard;





